/* Forte PDP — "how to use" pinned step-through.

   The how-to block sticks to the viewport while the reader scrolls a track
   sized to the number of steps, so there is time to read each one. As the
   track passes, the active step advances and its photo cross-fades in.

   The product card row above is NOT driven from here — which card is black is
   a property of the page (the product being viewed) and is decided in Liquid.
   Except in one-step mode (kit offer pages, [data-one-step]): there the how-to
   shows a single step under its product's name, the black card follows the
   active step, and tapping a card picks its step. Phones have no pin, so the
   cards are the only way to change step there; on desktop a tap scrolls the
   track to that step so the pin and the selection stay in agreement.

   Below the desktop breakpoint nothing can pin — the block plus the photo
   that hangs under the list is taller than a phone viewport — so the same
   advance runs as a scroll-spy instead: as each step crosses a reading line
   in the viewport, it becomes the active one and its photo cross-fades.

   Enhancement only. With JS off or reduced motion, no track height is set,
   nothing pins or spies, and step 1 stays active — the static comp. */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-fpdp-routine]');
  if (!roots.length) return;

  Array.prototype.forEach.call(roots, function (root) {
    var scroller = root.querySelector('[data-fpdp-rt-scroller]');
    var pin = root.querySelector('[data-fpdp-rt-pin]');
    var stepEls = [].slice.call(root.querySelectorAll('[data-fpdp-rt-step]'));
    var ruleEls = [].slice.call(root.querySelectorAll('[data-fpdp-rt-rule]'));
    var photos = [].slice.call(root.querySelectorAll('[data-fpdp-rt-photo]'));
    var heads = [].slice.call(root.querySelectorAll('[data-fpdp-rt-head]'));
    var oneStep = root.hasAttribute('data-one-step');
    var cards = oneStep ? [].slice.call(root.querySelectorAll('[data-fpdp-rt-card]')) : [];
    var picks = oneStep ? [].slice.call(root.querySelectorAll('[data-fpdp-rt-pick]')) : [];
    if (!scroller || !pin || stepEls.length < 2) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var wide = window.matchMedia('(min-width: 990px)');
    var count = stepEls.length;
    var active = -1;
    var ticking = false;

    function setActive(i) {
      if (i === active) return;
      active = i;
      stepEls.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      // The heavier rule is the one directly under the active step.
      ruleEls.forEach(function (el, n) { el.classList.toggle('is-strong', n === i); });
      photos.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      heads.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      cards.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      picks.forEach(function (el, n) { el.setAttribute('aria-pressed', n === i ? 'true' : 'false'); });
    }

    /* Without the pin there is nothing for the scroll to drive in one-step
       mode — only one step is on screen — so the step stays where the cards
       last put it. */
    function idle() {
      if (oneStep) { if (active < 0) setActive(0); return; }
      if (wanted()) { spy(); } else { setActive(0); }
    }

    function wanted() {
      return root.dataset.scroll === 'true' && !reduce.matches;
    }

    function enabled() {
      return wanted() && wide.matches;
    }

    /* Mobile: the active step is the last one whose top has crossed a reading
       line 40% down the viewport, so the highlight advances as each step
       reaches it and rewinds on the way back up. */
    function spy() {
      var line = (window.innerHeight || document.documentElement.clientHeight) * 0.4;
      var idx = 0;
      for (var i = 0; i < stepEls.length; i++) {
        if (stepEls[i].getBoundingClientRect().top <= line) idx = i;
      }
      setActive(idx);
    }

    /* The sticky ATC bar is fixed, so park the pin clear of it. Queried lazily:
       that bar renders later in the template than this section, so it may not
       exist when this first runs. The bar can be docked either end now — read
       which from its box, since a bottom-docked bar's .bottom is the full
       viewport height and would push the pin straight off the screen. */
    function offset() {
      var bar = document.querySelector('[data-fpdp-sticky]');
      var barBottom = 0;
      var barTopH = 0;
      if (bar && !bar.hidden) {
        var box = bar.getBoundingClientRect();
        if (box.height > 0) {
          if (box.top <= 1) {
            if (box.bottom > 0) barBottom = Math.round(box.bottom);
          } else {
            // Bottom-docked: nothing to clear above, but it eats usable height.
            barTopH = Math.round(box.height);
          }
        }
      }

      /* Minimum clearance: hard against the bar when it is out, or the section's
         own 24 when it is not. */
      var gap = barBottom ? 16 : 24;

      /* The block is a fixed 456 tall while the viewport is not, so parking it
         at that minimum leaves everything below it empty — 442px of void on a
         1080 screen, and more on a taller one. Centring it in what is left under
         the bar splits that height above and below instead, which reads as
         margin. Falls back to the minimum when the block is taller than the
         space, so a short window still clears the bar. */
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var spare = Math.round((vh - barBottom - barTopH - pin.offsetHeight) / 2);
      if (spare > gap) gap = spare;

      return barBottom + gap;
    }

    /* The bar reveals on scroll — after measure() has already run — so the pin's
       top has to follow it rather than being set once at load. Applied only when
       the value actually changes, because update() runs on every frame. */
    var appliedTop = null;

    function syncTop(top) {
      if (top === appliedTop) return;
      appliedTop = top;
      pin.style.top = top + 'px';
    }

    /* Track height = one pin-height to hold the block still, plus a slice of
       scrolling per step after the first. */
    function measure() {
      if (!enabled()) {
        root.classList.remove('is-pinned');
        scroller.style.minHeight = '';
        pin.style.top = '';
        appliedTop = null;
        idle();
        return;
      }
      root.classList.add('is-pinned');
      syncTop(offset());

      var vh = window.innerHeight || document.documentElement.clientHeight;
      var per = parseInt(root.dataset.distance, 10) || 60;   // vh per step
      var slice = vh * per / 100;

      /* A slice measured purely in vh scales with the window rather than with
         what is being revealed, so a tall monitor buys nothing but dead scroll:
         at 1080 the track came to 2400 for a 456-tall block, and the section to
         2914. Capped against the block instead — one step never costs more than
         roughly a block-height of scrolling — which keeps the pacing identical
         on a laptop and stops it inflating above that. */
      var cap = pin.offsetHeight * 0.9;
      if (slice > cap) slice = cap;

      scroller.style.minHeight = Math.round(pin.offsetHeight + (count - 1) * slice) + 'px';
      update();
    }

    function update() {
      if (!enabled()) {
        idle();
        return;
      }

      // Re-read the bar every frame so the pin parks below it the moment it
      // slides in, and moves back up when it slides away.
      var off = offset();
      syncTop(off);

      var rect = scroller.getBoundingClientRect();
      var travel = rect.height - pin.offsetHeight;
      if (travel <= 0) { setActive(0); return; }

      // 0 when the pin first locks, 1 when the track has finished passing.
      var p = (off - rect.top) / travel;
      if (p < 0) p = 0;
      if (p > 0.99999) p = 0.99999;
      setActive(Math.floor(p * count));
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { update(); ticking = false; });
    }

    picks.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.getAttribute('data-fpdp-rt-pick'), 10) || 0;
        if (!enabled()) { setActive(i); return; }
        // Pinned: scroll to the middle of step i's slice of the track.
        var rect = scroller.getBoundingClientRect();
        var travel = rect.height - pin.offsetHeight;
        var y = window.pageYOffset + rect.top - offset() + travel * (i + 0.5) / count;
        window.scrollTo({ top: Math.round(y), behavior: reduce.matches ? 'auto' : 'smooth' });
      });
    });

    // Hands photo opacity over from the CSS fallback to .is-active.
    root.classList.add('is-live');

    measure();
    if (document.readyState !== 'complete') window.addEventListener('load', measure);

    window.addEventListener('scroll', onScroll, { passive: true });
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(measure, 200);
    });
    if (reduce.addEventListener) reduce.addEventListener('change', measure);
    if (wide.addEventListener) wide.addEventListener('change', measure);
  });
})();
